import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import {
  ShieldAlert, ShieldCheck, ShieldQuestion, Flame, TrendingUp, TrendingDown, Copy, ExternalLink,
  Plus, Star, Sparkles, Users, Boxes, Activity, RefreshCw, History, Twitter,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtUsd, fmtPrice, fmtPct, fmtNum, CHAIN_META } from "@/lib/format";
import { XProfileCard } from "@/components/XProfileCard";
import { DevSoldPanel } from "@/components/DevSoldPanel";

const LEVEL = {
  low: { text: "text-emerald-400", bg: "bg-emerald-500/12", border: "border-emerald-500/40", bar: "#00FF66", glow: "shadow-[0_0_28px_rgba(0,255,102,0.25)]" },
  medium: { text: "text-amber-400", bg: "bg-amber-500/12", border: "border-amber-500/40", bar: "#FFB800", glow: "shadow-[0_0_28px_rgba(255,184,0,0.2)]" },
  high: { text: "text-red-400", bg: "bg-red-500/12", border: "border-red-500/40", bar: "#FF3355", glow: "shadow-[0_0_28px_rgba(255,51,85,0.22)]" },
  extreme: { text: "text-red-500", bg: "bg-red-500/16", border: "border-red-500/50", bar: "#FF1744", glow: "shadow-[0_0_32px_rgba(255,23,68,0.3)]" },
};

const flagIcon = (s) =>
  s === "pass" ? <ShieldCheck className="h-4 w-4 text-emerald-400" /> :
  s === "warn" ? <ShieldQuestion className="h-4 w-4 text-amber-400" /> :
  <ShieldAlert className="h-4 w-4 text-red-400" />;

function Widget({ title, icon: Icon, children, testid, className = "", id }) {
  return (
    <Card id={id} className={`bg-[#111723] border-[#222F43] p-4 sm:p-6 scroll-mt-[9.5rem] md:scroll-mt-24 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-cyan-400" />
        <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

const SECTIONS = [
  ["sec-metrics", "Metrics"], ["sec-risk", "Risk"], ["sec-chart", "Chart"], ["sec-holders", "Holders"],
  ["sec-bundles", "Bundles"], ["sec-dev", "Dev"], ["sec-txns", "Flow"], ["sec-socials", "Socials"], ["sec-ai", "AI Take"],
];

function MobileReportHeader({ token, chain, P, pc, up, risk, lv, active, onJump }) {
  return (
    <div className="md:hidden sticky top-14 z-30 -mx-4 px-4 pt-2 pb-2 glass border-b border-[#222F43]" data-testid="mobile-report-header">
      <div className="flex items-center gap-3">
        {token.imageUrl ? <img src={token.imageUrl} alt="" className="h-10 w-10 rounded-full border border-[#334566]" /> :
          <div className="h-10 w-10 rounded-full bg-[#172030] border border-[#334566] flex items-center justify-center font-display font-bold text-emerald-400 text-sm">{token.symbol?.slice(0, 2)}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-extrabold text-lg leading-none truncate">${token.symbol}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border" style={{ borderColor: chain.color + "66", color: chain.color }}>{chain.label}</span>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-mono font-bold text-sm">{fmtPrice(P)}</span>
            <span className={`font-mono text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>{fmtPct(pc.h24)} 24h</span>
          </div>
        </div>
        <div className={`shrink-0 text-right rounded-xl border px-2.5 py-1.5 ${lv.border} ${lv.bg}`} data-testid="mobile-risk-pill">
          <div className={`font-mono font-extrabold text-xl leading-none ${lv.text}`}>{risk.score}</div>
          <div className={`text-[9px] font-mono uppercase tracking-wider mt-0.5 ${lv.text}`}>{risk.category}</div>
        </div>
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar mt-2.5 -mx-4 px-4" data-testid="mobile-section-chips">
        {SECTIONS.map(([id, label]) => (
          <button key={id} onClick={() => onJump(id)} data-testid={`chip-${id}`}
            className={`tap shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${active === id ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : id === "sec-ai" ? "text-emerald-300 border-emerald-500/30" : "text-slate-400 border-[#222F43]"}`}>
            {id === "sec-ai" && <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" />}{label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MobileActionBar({ onCopy, onLog, onWatch, tradeUrl }) {
  return createPortal(
    <div className="md:hidden fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom, 0px))" }} data-testid="mobile-action-bar">
      <div className="glass border border-[#334566] rounded-2xl p-1.5 flex items-center gap-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
        {[[onCopy, Copy, "CA", "m-copy-ca"], [onWatch, Star, "Watch", "m-watch"], [onLog, Plus, "Log", "m-log-trade"]].map(([fn, Icon, label, tid]) => (
          <button key={tid} onClick={fn} data-testid={tid} className="tap flex-1 h-11 rounded-xl flex flex-col items-center justify-center text-[10px] font-semibold text-slate-300 bg-[#172030]">
            <Icon className="h-4 w-4 mb-0.5" /> {label}
          </button>
        ))}
        {tradeUrl && (
          <a href={tradeUrl} target="_blank" rel="noreferrer" data-testid="m-trade" className="tap flex-[1.6] h-11 rounded-xl flex items-center justify-center gap-1.5 text-sm font-display font-extrabold text-black bg-emerald-500 shadow-[0_0_18px_rgba(0,255,102,0.35)]">
            Trade <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>,
    document.body,
  );
}

const pctOrNA = (v) => (v == null ? "N/A" : `${v}%`);

function MetricTile({ label, value, sub, subColor = "text-slate-500", valueColor = "", testid }) {
  const isNA = value === null || value === undefined || value === "";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 leading-tight">{label}</div>
      <div className={`font-mono font-bold text-lg sm:text-xl mt-1 ${isNA ? "text-slate-600" : valueColor}`}>{isNA ? "N/A" : value}</div>
      {sub != null && !isNA && <div className={`text-[11px] font-mono mt-0.5 ${subColor}`}>{sub}</div>}
    </div>
  );
}

function AuditRow({ label, value, testid }) {
  const isNA = value === null || value === undefined || value === "";
  return (
    <div className="flex items-center justify-between gap-3 text-sm" data-testid={testid}>
      <span className="text-slate-300">{label}</span>
      <span className={`font-mono text-right ${isNA ? "text-slate-600" : "text-slate-100"}`}>{isNA ? "N/A" : value}</span>
    </div>
  );
}

function ageFrom(ms) {
  if (!ms) return null;
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  const h = Math.floor(s / 3600);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${(d / 365).toFixed(1)}y`;
}

function volFlag(ratio) {
  if (ratio == null) return null;
  const label = ratio < 0.1 ? "quiet" : ratio <= 3 ? "normal" : "high";
  return `${ratio}x — ${label}`;
}

function SourceTag({ src }) {
  if (src === "birdeye")
    return <p className="text-[10px] mt-2 font-mono text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block pulse-glow" /> LIVE · Birdeye on-chain</p>;
  if (src === "rugcheck")
    return <p className="text-[10px] mt-2 font-mono text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block pulse-glow" /> LIVE · RugCheck on-chain</p>;
  if (src === "goplus")
    return <p className="text-[10px] mt-2 font-mono text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block pulse-glow" /> LIVE · GoPlus on-chain</p>;
  if (src === "unavailable")
    return <p className="text-[10px] mt-2 font-mono text-slate-500">No live source available · try refreshing in a minute</p>;
  return <p className="text-[10px] text-slate-600 mt-2 font-mono">* estimate — no live source for this chain yet</p>;
}

export default function TokenReport({ report, onRefresh, refreshing }) {  const { token, risk, holders, bundles } = report;
  const dataSources = report.dataSources || [];
  const lv = LEVEL[risk.level] || LEVEL.medium;
  const nav = useNavigate();
  const { user, openAuth } = useAuth();
  const [verdict, setVerdict] = useState(null);
  const [vLoading, setVLoading] = useState(false);

  const pc = token.priceChange || {};
  const P = token.priceUsd || 0;
  const series = [
    { t: "24h", p: P / (1 + (pc.h24 || 0) / 100) },
    { t: "6h", p: P / (1 + (pc.h6 || 0) / 100) },
    { t: "1h", p: P / (1 + (pc.h1 || 0) / 100) },
    { t: "5m", p: P / (1 + (pc.m5 || 0) / 100) },
    { t: "now", p: P },
  ];
  const up = (pc.h24 || 0) >= 0;
  const txns24 = token.txns?.h24 || {};
  const buys = txns24.buys || 0, sells = txns24.sells || 0;
  const buyPct = risk.buyPressurePct ?? 50;

  const audit = report.audit || {};
  const creator = report.creator || {};
  const socials = report.socials || {};
  const ath = report.ath || {};
  const uniques = report.uniques || {};
  const mc = token.marketCap || token.fdv;
  const vol24 = token.volume?.h24;
  const volRatio = mc ? Math.round((vol24 / mc) * 100) / 100 : null;
  const age = ageFrom(token.pairCreatedAt);
  const pctOr = (v) => (v === null || v === undefined ? null : `${v}%`);
  const quote = report.quote;
  const quoteLabel = quote?.symbol || null;
  const quoteTracks = quote?.tracks || null;

  useEffect(() => { setVerdict(null); setVLoading(false); }, [token.address, token.chain]);

  const getVerdict = () => {
    setVLoading(true);
    api.post("/token/verdict", {
      symbol: token.symbol, name: token.name, chain: token.chain, address: token.address, score: risk.score,
      category: risk.category, flags: risk.flags, priceChange24: pc.h24 || 0,
      liquidityUsd: token.liquidityUsd, marketCap: token.marketCap || token.fdv,
      holders, bundles, audit, ageLabel: age, x: socials.twitter || {}, devSold: creator.devSold || {},
    }).then(({ data }) => setVerdict(data))
      .catch((err) => {
        if (err.response?.status === 402) toast.error("AI Take is a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } });
        else toast.error("AI Take unavailable right now");
      })
      .finally(() => setVLoading(false));
  };

  const copyCA = () => { navigator.clipboard.writeText(token.address); toast.success("Contract address copied"); };

  const addWatch = async () => {
    if (!user) { openAuth(); return; }
    try {
      const { data } = await api.post("/watchlist", {
        symbol: token.symbol, name: token.name, chain: token.chain, address: token.address, imageUrl: token.imageUrl,
      });
      toast.success(data.duplicate ? "Already in your watchlist" : `${token.symbol} added to watchlist`);
    } catch (err) {
      if (err.response?.status === 402) toast.error("Watchlist is a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } });
      else toast.error("Could not add to watchlist");
    }
  };

  const logTrade = () => {
    if (!user) { openAuth(); return; }
    nav("/journal", { state: { prefill: { ticker: token.symbol, name: token.name, address: token.address, chain: token.chain, buyPrice: P } } });
  };

  const chain = CHAIN_META[token.chain] || { label: token.chain, color: "#94A3B8" };
  const [activeSec, setActiveSec] = useState("sec-metrics");
  const jumpLock = useRef(0);
  const jump = (id) => {
    setActiveSec(id);
    jumpLock.current = Date.now() + 900;
    if (id === "sec-ai" && !verdict && !vLoading) getVerdict();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    document.querySelector(`[data-testid="chip-${activeSec}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeSec]);
  useEffect(() => {
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const obs = new IntersectionObserver((entries) => {
      if (Date.now() < jumpLock.current) return;
      const vis = entries.filter((x) => x.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActiveSec(vis[0].target.id);
    }, { rootMargin: "-160px 0px -55% 0px", threshold: 0 });
    SECTIONS.forEach(([id]) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [token.address]);

  return (
    <div className="space-y-3 sm:space-y-6 fade-up pb-20 md:pb-0">
      <MobileReportHeader token={token} chain={chain} P={P} pc={pc} up={up} risk={risk} lv={lv} active={activeSec} onJump={jump} />
      <MobileActionBar onCopy={copyCA} onLog={logTrade} onWatch={addWatch} tradeUrl={token.url} />
      {/* Header + actions */}
      <div className="hidden md:flex flex-wrap items-center gap-3">
        {token.imageUrl ? (
          <img src={token.imageUrl} alt="" className="h-12 w-12 rounded-full border border-[#334566]" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-[#172030] border border-[#334566] flex items-center justify-center font-display font-bold text-emerald-400">
            {token.symbol?.slice(0, 2)}
          </div>
        )}
        <div className="mr-auto">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-2xl sm:text-3xl font-bold">${token.symbol}</h2>
            <Badge variant="outline" style={{ borderColor: chain.color + "66", color: chain.color }} className="bg-transparent text-[10px] uppercase">
              {chain.label}
            </Badge>
            <Badge variant="outline" className="bg-transparent border-[#334566] text-slate-400 text-[10px] uppercase">{token.dexId}</Badge>
            {quoteLabel && (
              <Badge variant="outline" className="bg-transparent border-cyan-500/40 text-cyan-400 text-[10px] uppercase" data-testid="quote-badge">
                vs {quoteLabel}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-400">{token.name}</p>
          <p className="text-[10px] font-mono text-slate-500 mt-0.5">Data: {dataSources.join(" + ")}{(dataSources.includes("RugCheck") || dataSources.includes("Birdeye")) && <span className="text-emerald-400"> · live on-chain</span>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={copyCA} size="sm" variant="outline" className="border-[#334566] bg-transparent" data-testid="copy-ca-btn">
            <Copy className="h-4 w-4 mr-1.5" /> Copy CA
          </Button>
          <Button onClick={logTrade} size="sm" variant="outline" className="border-[#334566] bg-transparent" data-testid="log-trade-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Log Trade
          </Button>
          <Button onClick={addWatch} size="sm" variant="outline" className="border-[#334566] bg-transparent" data-testid="add-watchlist-btn">
            <Star className="h-4 w-4 mr-1.5" /> Watchlist
          </Button>
          {token.url && (
            <a href={token.url} target="_blank" rel="noreferrer">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="ape-dex-btn">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Trade
              </Button>
            </a>
          )}
          <Button variant="outline" size="icon" onClick={onRefresh} className="border-[#334566] bg-transparent" data-testid="refresh-report-btn">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KEY METRICS — the facts, above the fold */}
      <Card id="sec-metrics" className="bg-[#111723] border-[#222F43] p-4 sm:p-6 scroll-mt-[9.5rem] md:scroll-mt-24" data-testid="key-metrics">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-4 w-4 text-cyan-400" />
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Key Metrics — the facts</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5" data-testid="price-stats">
          <MetricTile label="Paired With" value={quoteLabel} valueColor="text-cyan-400"
            sub={quoteTracks ? `tracks ${quoteTracks}${(token.allQuotes || []).length > 1 ? ` · also ${token.allQuotes.filter((q) => q !== quoteLabel).slice(0, 2).join(", ")}` : ""}` : (token.allQuotes || []).length > 1 ? `main market · also ${token.allQuotes.filter((q) => q !== quoteLabel).slice(0, 2).join(", ")}` : (quoteLabel ? "main market" : null)}
            subColor="text-slate-500" testid="metric-quote" />
          <MetricTile label="Price" value={fmtPrice(P)} sub={fmtPct(pc.h24) + " · 24h"} subColor={up ? "text-emerald-400" : "text-red-400"} />
          <MetricTile label="Market Cap" value={fmtUsd(token.marketCap || token.fdv)} sub={token.marketCapSource === "birdeye" ? "via Birdeye" : "via DexScreener"} testid="metric-mcap" />
          <MetricTile label="Liquidity" value={fmtUsd(token.liquidityUsd)} />
          <MetricTile label="Volume 24h" value={fmtUsd(token.volume?.h24)} sub={volFlag(volRatio)} subColor="text-slate-500" />
          <MetricTile label="FDV" value={fmtUsd(token.fdv)} />
          <MetricTile label="Coin Age" value={age} sub={age ? "since pair created" : null} subColor="text-slate-500" testid="metric-age" />
          <MetricTile label="ATH" value={ath.price != null ? fmtPrice(ath.price) : null} sub={ath.marketCap != null ? `${fmtUsd(ath.marketCap)} mcap` : null} subColor="text-slate-500" testid="metric-ath" />
          <MetricTile label="Rug Risk (0-100)" value={`${risk.score}`} valueColor={lv.text} sub={risk.category + " · higher = riskier"} subColor={lv.text} testid="risk-score" />
          <MetricTile label="Holders" value={fmtNum(holders.holderCount)} sub={holders.holderCount == null ? "unavailable" : "live"} subColor="text-slate-500" testid="metric-holders" />
          <MetricTile label="Top 10 Holders" value={pctOrNA(holders.top10Pct)} valueColor="text-amber-400" testid="metric-top10" />
          <MetricTile label="Insiders" value={pctOrNA(holders.insiderPct ?? holders.devPct)} valueColor="text-red-400" />
          <MetricTile label="LP / Pools" value={pctOrNA(holders.lpPct ?? holders.burnPct)} valueColor="text-cyan-400" />
          <MetricTile label="Bundled (now)" value={pctOrNA(bundles.currentHeldPct ?? bundles.bundledSupplyPct)} valueColor="text-purple-400" sub={bundles.bundledSupplyPct == null ? "unavailable" : `${bundles.bundledSupplyPct}% at launch · ${bundles.bundleCount ?? 0} ${bundles.source === "birdeye" ? "wallets" : "clusters"}`} subColor="text-slate-500" testid="metric-bundled" />
          <MetricTile label="Buy Pressure 24h" value={`${buyPct}%`} valueColor="text-emerald-400" sub={`${fmtNum(buys)} buys · ${fmtNum(sells)} sells`} subColor="text-slate-500" />
        </div>
        {/* price change strip */}
        <div className="grid grid-cols-4 gap-3 mt-5 pt-4 border-t border-[#222F43]">
          {[["5m", pc.m5], ["1h", pc.h1], ["6h", pc.h6], ["24h", pc.h24]].map(([lbl, v]) => (
            <div key={lbl} className="text-center">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{lbl}</div>
              <div className={`font-mono font-bold text-sm mt-0.5 ${(v || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtPct(v)}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Rug Risk & Security Audit — moved up, ahead of the chart */}
      <Widget id="sec-risk" title="Rug Risk & Security Audit" icon={ShieldAlert} testid="security-audit">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-2.5">
          {risk.flags.map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm" data-testid={`flag-${i}`}>
              {flagIcon(f.status)}
              <span className="text-slate-300 w-28 sm:w-32 shrink-0">{f.label}</span>
              <span className={`${f.status === "fail" ? "text-red-400" : f.status === "warn" ? "text-amber-400" : "text-slate-400"} text-xs`}>
                {f.detail}
              </span>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-2.5 mt-4 pt-4 border-t border-[#222F43]">
          <AuditRow label="Dev Wallet %" value={pctOr(audit.devWalletPct)} testid="audit-dev-pct" />
          <AuditRow label="Dev Sold Since Launch" value={creator.devSold?.source === "helius" && !["never_held", "unknown"].includes(creator.devSold.status) ? `${(creator.devSold.status.startsWith("linked_") ? creator.devSold.linked?.soldPct : creator.devSold.soldPct) ?? "N/A"}% of bag` : null} testid="audit-dev-sold" />
          <AuditRow label="LP Burned (permanent)" value={pctOr(audit.lpBurnedPct)} testid="audit-lp-burned" />
          <AuditRow label="LP Locked" value={audit.lpLockedPct != null ? `${audit.lpLockedPct}%${audit.lpLockedUntil ? ` until ${audit.lpLockedUntil}` : ""}` : null} testid="audit-lp-locked" />
          <AuditRow label="DEX Paid" value={audit.dexPaid === true ? "Yes" : audit.dexPaid === false ? "No" : null} testid="audit-dex-paid" />
          <AuditRow label="Can Sell? (honeypot sim)" value={audit.canSell === true ? "Yes" : audit.canSell === false ? "No" : null} testid="audit-honeypot" />
          <AuditRow label="Buy Tax" value={pctOr(audit.buyTax)} testid="audit-buy-tax" />
          <AuditRow label="Sell Tax" value={pctOr(audit.sellTax)} testid="audit-sell-tax" />
        </div>
      </Widget>

      {/* Price Trend chart */}
      <Widget id="sec-chart" title="Price Trend (24h reconstruct)" icon={up ? TrendingUp : TrendingDown} testid="price-chart">
        <div className="h-52">
          <ResponsiveContainer width="99%" height="100%">
            <AreaChart data={series} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={up ? "#00FF66" : "#FF3355"} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={up ? "#00FF66" : "#FF3355"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Tooltip contentStyle={{ background: "#0d1420", border: "1px solid #334566", borderRadius: 8, fontSize: 12 }}
                       labelStyle={{ color: "#94A3B8" }} formatter={(v) => [fmtPrice(v), "price"]} />
              <Area type="monotone" dataKey="p" stroke={up ? "#00FF66" : "#FF3355"} strokeWidth={2} fill="url(#pg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Widget>

      {/* Holders + Bundles + Creator + Txns */}
      <div className="grid lg:grid-cols-2 gap-3 sm:gap-6">
        <Widget id="sec-holders" title="Holders Distribution" icon={Users} testid="holders-widget">
          {holders.distribution.length === 0 && (
            <div className="h-40 flex items-center justify-center text-center text-xs text-slate-500 font-mono px-6" data-testid="holders-unavailable">
              Holder data isn't available for this token yet — no on-chain holder source answered.
            </div>
          )}
          <div className={holders.distribution.length === 0 ? "hidden" : "h-40"}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={holders.distribution} dataKey="pct" nameKey="label" innerRadius={40} outerRadius={64} paddingAngle={2} stroke="none">
                  {holders.distribution.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0d1420", border: "1px solid #334566", borderRadius: 8, fontSize: 12 }}
                         formatter={(v, n) => [`${v}%`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div><span className="text-slate-500">Holders</span> <span className="font-mono font-bold">{fmtNum(holders.holderCount)}</span></div>
            <div><span className="text-slate-500">Top 10</span> <span className="font-mono font-bold text-amber-400">{pctOrNA(holders.top10Pct)}</span></div>
            <div><span className="text-slate-500">Insiders</span> <span className="font-mono font-bold text-red-400">{pctOrNA(holders.insiderPct ?? holders.devPct)}</span></div>
            <div><span className="text-slate-500">LP / Pools</span> <span className="font-mono font-bold text-cyan-400">{pctOrNA(holders.lpPct ?? holders.burnPct)}</span></div>
          </div>
          <SourceTag src={holders.source} />
        </Widget>

        <Widget id="sec-bundles" title="Bundle Detection" icon={Boxes} testid="bundles-widget">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="font-mono font-extrabold text-3xl text-purple-400">{bundles.bundleCount ?? "—"}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{bundles.source === "birdeye" ? "launch wallets" : bundles.source === "rugcheck" ? "insider clusters" : "clusters"}</div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 flex-1">
              <div data-testid="current-bundle">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Current Bundle</div>
                <div className={`font-mono font-bold text-xl ${bundles.currentHeldPct == null ? "text-slate-600" : "text-amber-400"}`}>{bundles.currentHeldPct == null ? "N/A" : `${bundles.currentHeldPct}%`}</div>
                <div className="text-[10px] font-mono text-slate-500">{bundles.currentHeldPct == null ? "—" : "still held at scan time"}</div>
              </div>
              <div data-testid="launch-bundle">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Launch Bundle</div>
                <div className="font-mono font-bold text-xl text-purple-400">{bundles.bundledSupplyPct != null ? `${bundles.bundledSupplyPct}%` : <span className="text-slate-600">N/A</span>}</div>
                <div className="text-[10px] font-mono text-slate-500">{bundles.bundleCount == null ? "no data" : `${bundles.bundleCount} wallets at launch`}</div>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {bundles.clusters.length === 0 && (bundles.bundleCount == null
              ? <p className="text-xs text-slate-500">Bundle data isn't available for this token yet.</p>
              : <p className="text-xs text-emerald-400">No significant bundles detected 🎉</p>)}
            {bundles.clusters.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 w-16">#{c.id}{c.wallets > 1 ? ` · ${c.wallets}w` : ""}</span>
                <div className="flex-1 h-1.5 rounded-full bg-[#0d1420] overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: `${Math.min(100, c.supplyPct * 10)}%` }} />
                </div>
                <span className="font-mono text-purple-300 w-10 text-right">{c.supplyPct}%</span>
              </div>
            ))}
          </div>
          <SourceTag src={bundles.source} />
        </Widget>

        <Widget id="sec-dev" title="Creator — Deploy History & Dev Sold" icon={History} testid="creator-widget">
          <div className="flex items-center gap-4 mb-3">
            <div>
              <div className="font-mono font-extrabold text-3xl text-slate-100">{creator.deployedCount ?? <span className="text-slate-600">N/A</span>}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">tokens deployed</div>
            </div>
            <div className="text-xs space-y-1">
              <div><span className="text-slate-500">Look like rugs</span> <span className={`font-mono font-bold ${creator.rugCount == null ? "text-slate-600" : "text-red-400"}`}>{creator.rugCount ?? "N/A"}</span></div>
              <div className="text-slate-500">Creator: <span className="font-mono text-slate-300">{creator.address ? `${creator.address.slice(0, 4)}…${creator.address.slice(-4)}` : <span className="text-slate-600">N/A</span>}</span></div>
            </div>
          </div>
          {creator.deployedCount == null ? (
            <p className="text-[11px] text-slate-500 font-mono">Deploy history source not wired for this token yet — will populate automatically once available.</p>
          ) : creator.deployedCount === 0 ? (
            <p className="text-xs text-emerald-400">First token from this wallet 🌱</p>
          ) : (
            <p className="text-[11px] text-slate-500 font-mono">{creator.rugCount ?? 0} of {creator.deployedCount} prior tokens flagged low/dead (heuristic).</p>
          )}
          <DevSoldPanel ds={creator.devSold} chain={token.chain} />
        </Widget>

        <Widget id="sec-txns" title="Buy / Sell Pressure (24h)" icon={Activity} testid="txns-widget">
          <div className="flex items-end justify-between mb-2">
            <div><div className="font-mono font-bold text-2xl text-emerald-400">{fmtNum(buys)}</div><div className="text-[10px] text-slate-500 uppercase">buys</div></div>
            <Flame className="h-6 w-6 text-amber-400 pulse-glow" />
            <div className="text-right"><div className="font-mono font-bold text-2xl text-red-400">{fmtNum(sells)}</div><div className="text-[10px] text-slate-500 uppercase">sells</div></div>
          </div>
          <div className="h-3 rounded-full overflow-hidden flex bg-[#0d1420]">
            <div className="h-full bg-emerald-500" style={{ width: `${buyPct}%` }} />
            <div className="h-full bg-red-500" style={{ width: `${100 - buyPct}%` }} />
          </div>
          <div className="flex justify-between text-xs font-mono mt-1.5">
            <span className="text-emerald-400">{buyPct}% buy</span>
            <span className="text-red-400">{100 - buyPct}% sell</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-[#222F43]">
            <AuditRow label="Unique buyers" value={uniques.buyers} testid="uniq-buyers" />
            <AuditRow label="Unique sellers" value={uniques.sellers} testid="uniq-sellers" />
            <AuditRow label="Unique traders" value={uniques.traders24h != null ? fmtNum(uniques.traders24h) : null} testid="uniq-traders" />
          </div>
        </Widget>
      </div>

      {/* Socials */}
      <Widget id="sec-socials" title="Socials" icon={Twitter} testid="socials-widget">
        {/* Website — only a real project domain from the DexScreener websites array */}
        <div className="pb-3 mb-3 border-b border-[#222F43]" data-testid="social-website">
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-slate-300 shrink-0">Website</span>
            {socials.website?.url ? (
              <a href={socials.website.url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 hover:underline break-all text-right">
                {socials.website.url}
              </a>
            ) : (
              <span className="font-mono text-slate-600">N/A</span>
            )}
          </div>
          <div className="flex items-center justify-between gap-3 text-sm mt-1.5" data-testid="social-website-age">
            <span className="text-slate-300">Website Age</span>
            <span className={`font-mono ${socials.website?.ageDays != null ? "text-slate-100" : "text-slate-600"}`}>
              {socials.website?.ageDays != null ? `${socials.website.ageDays} days` : "N/A"}
            </span>
          </div>
          <p className="text-[10px] font-mono text-slate-500 mt-1">
            {socials.website?.url ? `from ${socials.website.source || "DexScreener profile"} · creator-supplied, not verified by us` : "No project website listed"}
          </p>
        </div>

        {/* X (Twitter) profile — via backend provider, cached 24h */}
        <div data-testid="social-twitter">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">X (Twitter)</div>
          <XProfileCard tw={socials.twitter} />
        </div>

        {/* Other social / platform links */}
        {(socials.links || []).filter((l) => l.type !== "X (Twitter)").length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#222F43]" data-testid="social-links">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Social / Platform Links</div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
              {(socials.links || []).filter((l) => l.type !== "X (Twitter)").map((l, i) => (
                <div key={i} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-slate-300 shrink-0">{l.type}</span>
                  <a href={l.url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 hover:underline break-all text-right">{l.url}</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </Widget>

      {/* AI Verdict — moved to the bottom: opinion, not the first thing */}
      <Card id="sec-ai" className={`bg-[#111723] border ${lv.border} ${lv.glow} p-4 sm:p-6 scroll-mt-[9.5rem] md:scroll-mt-24`} data-testid="ape-verdict-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">AI Take — opinion, not advice</span>
          <Badge className={`${lv.bg} ${lv.text} ${lv.border} border text-xs font-display font-extrabold px-3 py-0.5 rounded-full ml-1`} data-testid="verdict-badge">
            {risk.category}
          </Badge>
        </div>
        {vLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full bg-[#1a2333]" />
            <Skeleton className="h-4 w-11/12 bg-[#1a2333]" />
            <Skeleton className="h-4 w-3/4 bg-[#1a2333]" />
          </div>
        ) : verdict ? (
          <p className="text-slate-200 leading-relaxed text-[15px]" data-testid="verdict-summary">
            {verdict.summary}
            {!verdict.ai && <span className="block text-[10px] text-slate-500 mt-2 font-mono">heuristic summary</span>}
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-slate-400 flex-1">Want a plain-English read of the numbers above? Ask the AI for its 3-sentence take.</p>
            <Button onClick={getVerdict} data-testid="get-verdict-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-display font-extrabold px-5 shrink-0">
              <Sparkles className="h-4 w-4 mr-1.5" /> Get AI Take
            </Button>
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-3 font-mono">An AI read of the data above — the numbers are the facts, this is just an opinion. Your call.</p>
      </Card>
    </div>
  );
}
